import React, { useState } from 'react';
import { Checkbox, Flex, List, message, Switch } from 'antd';
import type { TelemetryVital } from '../../apis/telemetry';
import { useTelemetrySummary } from '../Shipments/SensorTimeline/queries';
import {
  availableCharts,
  CHART_MEASURES,
} from '../Shipments/SensorTimeline/lib/measures';
import styles from '../../assets/styles/CutomStyle/ShareLinkDisplay.module.css';
import CustomParagraph from '../../components/CustomParagraph';
import CustomInput from '../../components/CustomInput';
import CustomButton from '../../components/CustomButton';
import { useSpin } from '../../components/SpinContext';
import { ShareShipmentApi } from '../../apis/shipment';
import theme from '../../assets/theme';

interface ShareShipmentProps {
  shipmentId: string;
  token: string;
}

type ShareOptionKey =
  | 'showProductDetails'
  | 'showAlerts'
  | 'showSensorData'
  | 'maskTrackingLink'
  | 'requirePassword';

interface ShareOption {
  name: string;
  key: ShareOptionKey;
  checked: boolean;
  disabled?: boolean;
}

/** Options that only work on password-protected links. */
const PASSWORD_GATED: readonly ShareOptionKey[] = [
  'showProductDetails',
  'showAlerts',
];
const isPasswordGated = (key: ShareOptionKey): boolean =>
  PASSWORD_GATED.includes(key);

/**
 * ShareShipment component for generating and managing shareable shipment links
 *
 * @param shipmentId - The ID of the shipment to share
 * @param token - Authentication token for API calls
 */
const ShareShipment: React.FC<ShareShipmentProps> = ({ shipmentId, token }) => {
  const baseurl = theme.shareUrl;

  const { showSpin, hideSpin } = useSpin();

  const generateNewUUID = (): string => crypto.randomUUID();

  const [uuid, setUuid] = useState<string>(generateNewUUID());
  const [password, setPassword] = useState<string>('');
  // The link whose copy failed on every path, so it can be copied by hand. It is
  // kept apart from shareLink, which may have rotated while the copy was pending.
  const [manualCopyLink, setManualCopyLink] = useState<string | null>(null);

  const baseShareLink = `${baseurl}share/shipment/external-view/${shipmentId}/${uuid}`;
  const [shareLink, setShareLink] = useState<string>(baseShareLink);

  // Sensor values with readings; a failed load (e.g. no vitals permission) hides the option.
  const summary = useTelemetrySummary(shipmentId, { enabled: true });
  const availableVitals = availableCharts(summary.data);
  const sensorOptionHidden = summary.isError;
  // Unticked rather than ticked, so values that start reporting later are shared.
  const [uncheckedVitals, setUncheckedVitals] = useState<TelemetryVital[]>([]);

  const [list, setList] = useState<ShareOption[]>([
    {
      name: 'Show product details',
      key: 'showProductDetails',
      checked: false,
      disabled: true,
    },
    {
      name: 'Show alerts',
      key: 'showAlerts',
      checked: false,
      disabled: true,
    },
    {
      name: 'Show sensor data',
      key: 'showSensorData',
      checked: true,
    },
    {
      name: 'Mask tracking number',
      key: 'maskTrackingLink',
      checked: false,
    },
    {
      name: 'Require password',
      key: 'requirePassword',
      checked: false,
    },
  ]);

  const isChecked = (key: ShareOptionKey): boolean =>
    Boolean(list.find((item) => item.key === key)?.checked);

  const setSensorData = (checked: boolean): void => {
    setList((prev) =>
      prev.map((item) =>
        item.key === 'showSensorData' ? { ...item, checked } : item
      )
    );
  };

  const toggleVital = (vital: TelemetryVital, checked: boolean): void => {
    const unchecked = checked
      ? uncheckedVitals.filter((v) => v !== vital)
      : [...uncheckedVitals, vital];
    // Unticking the last value turns sensor data off; turning it back on re-ticks all.
    if (availableVitals.every((v) => unchecked.includes(v))) {
      setSensorData(false);
      setUncheckedVitals([]);
      return;
    }
    setUncheckedVitals(unchecked);
  };

  // The token is single-use on the backend, so every copied link needs a fresh
  // one before the next POST, whichever clipboard path did the copying.
  const rotateShareLink = (): void => {
    const newUuid = generateNewUUID();
    setUuid(newUuid);
    setManualCopyLink(null);
    setShareLink(
      `${baseurl}share/shipment/external-view/${shipmentId}/${newUuid}`
    );
  };

  const toggleSwitch = (key: ShareOptionKey): void => {
    const newList = [...list];
    const index = newList.findIndex((item) => item.key === key);
    newList[index].checked = !newList[index].checked;

    const isRequirePasswordIndex = newList.findIndex(
      (item) => item.key === 'requirePassword'
    );
    const isProductDetailsIndex = newList.findIndex(
      (item) => item.key === 'showProductDetails'
    );
    const isAlertsIndex = newList.findIndex(
      (item) => item.key === 'showAlerts'
    );

    if (newList[index].key === 'requirePassword') {
      const requirePasswordEnabled = newList[index].checked;
      setPassword('');
      newList.forEach((item) => {
        if (isPasswordGated(item.key)) {
          item.disabled = !requirePasswordEnabled;
          if (!requirePasswordEnabled) {
            item.checked = false;
          }
        }
      });

      rotateShareLink();
    } else if (
      newList[index].key === 'showProductDetails' ||
      newList[index].key === 'showAlerts'
    ) {
      if (!newList[isRequirePasswordIndex].checked) {
        newList[isRequirePasswordIndex].checked = true;
        newList.forEach((item) => {
          if (isPasswordGated(item.key)) {
            item.disabled = false;
          }
        });
      }
      if (
        !newList[isProductDetailsIndex].checked &&
        !newList[isAlertsIndex].checked
      ) {
        newList[isRequirePasswordIndex].checked = false;
        newList.forEach((item) => {
          if (isPasswordGated(item.key)) {
            item.disabled = true;
            item.checked = false;
          }
        });
      }
    } else if (newList[index].key === 'showSensorData') {
      setUncheckedVitals([]);
    }

    setList(newList);
  };

  const handleCopyLink = (): void => {
    const onCopied = (): void => {
      message.success('Link copied to clipboard!');
      rotateShareLink();
    };
    // The link is not rotated on failure: it is the one the backend just stored,
    // so it is shown in a selectable field for the sender to copy by hand.
    const failedLink = shareLink;
    const onCopyFailed = (): void => {
      message.error('Copy failed. Please copy manually.');
      setManualCopyLink(failedLink);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(shareLink)
        .then(onCopied)
        .catch(() => {
          if (fallbackCopyToClipboard(shareLink)) {
            onCopied();
          } else {
            onCopyFailed();
          }
        });
    } else if (fallbackCopyToClipboard(shareLink)) {
      onCopied();
    } else {
      onCopyFailed();
    }
  };

  const fallbackCopyToClipboard = (text: string): boolean => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      copied = false;
    }

    document.body.removeChild(textarea);
    return copied;
  };

  const copyToClipboard = (): void => {
    const requirePassword = list.find(
      (item) => item.key === 'requirePassword'
    )?.checked;
    const showProductDetails = list.find(
      (item) => item.key === 'showProductDetails'
    )?.checked;
    const showAlerts = list.find((item) => item.key === 'showAlerts')?.checked;

    if (requirePassword) {
      if (!password) {
        message.error('Please enter a password before copying the link.');
        return;
      }
      if (password.length < 6) {
        message.error('Password must be at least 6 characters long.');
        return;
      }
    }

    // The switch starts on, so a link whose sensor row was hidden by a failed
    // summary keeps the default; a sender who already switched it off stays off.
    const showSensorData = isChecked('showSensorData');
    const payload = {
      shipment: shipmentId,
      show_product_details: showProductDetails || false,
      show_alerts: showAlerts || false,
      show_sensor_data: showSensorData,
      hidden_vitals: showSensorData
        ? availableVitals.filter((v) => uncheckedVitals.includes(v))
        : [],
      mask_tracking_link: isChecked('maskTrackingLink'),
      require_password: requirePassword || false,
      password: requirePassword ? password : null,
      token: uuid,
    };

    showSpin();
    ShareShipmentApi(token, payload)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            setTimeout(() => {
              handleCopyLink();
            }, 300);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch(() => {
        message.error('Error while generating the shareable link.');
      })
      .finally(() => {
        hideSpin();
      });
  };

  return (
    <>
      <List
        className='custom-list-container'
        itemLayout='horizontal'
        dataSource={list.filter(
          (item) => item.key !== 'showSensorData' || !sensorOptionHidden
        )}
        renderItem={(item) => {
          const row = (
            <>
              <CustomParagraph
                text={item.name}
                fontSize='14px'
                fontWeight={400}
                lineHeight='20px'
                color='var(--text-primary)'
                marginBottom='0px'
              />

              <Switch
                checked={item.checked}
                onChange={() => toggleSwitch(item.key)}
                style={{ marginLeft: '10px' }}
                aria-label={item.name}
              />
            </>
          );
          if (item.key !== 'showSensorData' || !item.checked) {
            return (
              <List.Item className='custom-radio-list-item'>{row}</List.Item>
            );
          }
          return (
            <List.Item className='custom-radio-list-item'>
              <Flex vertical gap={12} style={{ width: '100%' }}>
                <Flex justify='space-between' align='center'>
                  {row}
                </Flex>
                {summary.isSuccess && availableVitals.length === 0 && (
                  <CustomParagraph
                    text='No sensor readings yet. All values will be shared as they arrive.'
                    fontSize='12px'
                    fontWeight={400}
                    lineHeight='16px'
                    color='var(--text-tertiary)'
                    marginBottom='0px'
                  />
                )}
                {availableVitals.length > 0 && (
                  <Flex
                    vertical
                    gap={12}
                    role='group'
                    aria-label='Sensor values to share'
                    style={{ paddingLeft: '24px' }}
                  >
                    {availableVitals.map((vital) => (
                      <Checkbox
                        key={vital}
                        checked={!uncheckedVitals.includes(vital)}
                        onChange={(event) =>
                          toggleVital(vital, event.target.checked)
                        }
                      >
                        {CHART_MEASURES[vital].label}
                      </Checkbox>
                    ))}
                  </Flex>
                )}
              </Flex>
            </List.Item>
          );
        }}
        style={{ margin: '0 0 12px' }}
      ></List>

      {list.find((item) => item.key === 'requirePassword')?.checked && (
        <Flex vertical style={{ marginBottom: '20px' }}>
          <CustomInput
            placeholder="Set password you'll share to open the tracking link"
            type='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            height='36px'
            lineHeight='20px'
          />
        </Flex>
      )}

      <Flex justify='space-between' gap={16} align='center'>
        <div className={styles.wrap}>
          {manualCopyLink !== null ? (
            <input
              className={`${styles.box} ${styles.selectable}`}
              aria-label='Share link'
              readOnly
              value={manualCopyLink}
              onFocus={(e) => e.currentTarget.select()}
            />
          ) : (
            <div
              className={styles.box}
              title={shareLink}
              onContextMenu={(e) => e.preventDefault()}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
            >
              <span className={styles.text}>{shareLink}</span>
              <span className={styles.gutter} aria-hidden='true' />
            </div>
          )}
        </div>

        <CustomButton
          onClick={copyToClipboard}
          text='Copy Link'
          height='36px'
          width='85px'
          fontSize='14px'
          fontWeight='500'
          color='#fff'
          bgColor='#000'
        />
      </Flex>
    </>
  );
};

export default ShareShipment;
